import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";

const _ = null;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!stop) setState("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          if (!stop) setState("failed");
          return;
        }
      } catch (e) {
        if (!stop && e?.response?.status === 404) {
          setState("failed");
          return;
        }
      }
      tries.current += 1;
      if (stop) return;
      if (tries.current > 10) {
        setState("pending");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stop = true;
    };
  }, [sessionId]);

  return (
    <div className="flex min-h-[100svh] items-center justify-center px-5 pt-24">
      <div className="w-full max-w-md rounded-2xl border border-white/[0.07] bg-fog/60 p-10 text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 size={28} className="mx-auto animate-spin text-cyanx" />
            <h1 className="mt-6 font-serif text-3xl font-light text-ink">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-dim">This only takes a moment.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 size={38} className="mx-auto text-cyanx" />
            <h1 className="mt-6 font-serif text-3xl font-light text-ink">Thank you.</h1>
            <p className="mt-3 text-sm leading-relaxed text-dim">
              Your purchase is confirmed — your receipt is on its way from Stripe, and Isabella
              has been notified of your direct support.
            </p>
            <Link
              to="/books"
              className="mt-8 inline-block rounded-full bg-cyanx px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.24em] text-void"
              data-testid="payment-success-books-link"
            >
              Explore the books
            </Link>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="payment-failed">
            <XCircle size={38} className="mx-auto text-ember" />
            <h1 className="mt-6 font-serif text-3xl font-light text-ink">Payment not completed.</h1>
            <p className="mt-3 text-sm leading-relaxed text-dim">
              Nothing was charged. You can try again any time.
            </p>
            <Link
              to="/books"
              className="mt-8 inline-block rounded-full border border-white/15 px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.24em] text-ink hover:border-cyanx/60 hover:text-cyanx"
            >
              Back to the books
            </Link>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 size={28} className="mx-auto animate-spin text-cyanx" />
            <h1 className="mt-6 font-serif text-3xl font-light text-ink">Almost there.</h1>
            <p className="mt-3 text-sm leading-relaxed text-dim">
              Your payment is still confirming with Stripe. You'll receive your receipt by email —
              no need to stay on this page.
            </p>
            <Link
              to="/"
              className="mt-8 inline-block rounded-full border border-white/15 px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.24em] text-ink hover:border-cyanx/60 hover:text-cyanx"
            >
              Back home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-[100svh] items-center justify-center px-5 pt-24">
      <div className="w-full max-w-md rounded-2xl border border-white/[0.07] bg-fog/60 p-10 text-center" data-testid="payment-cancelled">
        <XCircle size={38} className="mx-auto text-ember" />
        <h1 className="mt-6 font-serif text-3xl font-light text-ink">Checkout cancelled.</h1>
        <p className="mt-3 text-sm leading-relaxed text-dim">
          No charge was made. The books will be right here when you're ready.
        </p>
        <Link
          to="/books"
          className="mt-8 inline-block rounded-full bg-cyanx px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.24em] text-void"
          data-testid="payment-cancel-books-link"
        >
          Back to the books
        </Link>
      </div>
    </div>
  );
}
